import { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import * as THREE from 'three';
import { 
    TilesRenderer, 
    GLTFMeshFeaturesExtension, 
    GLTFStructuralMetadataExtension, 
} from '3d-tiles-renderer';
import gui from "../utils/gui";
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { loadTileset } from '../utils/loadingManager';
const TileSet = forwardRef(({
  url,
  scene,
  renderer,
  camera,
  position = { x: 0, y: 12, z: 0 },
  rotation = { x: -0.562, y: 0, z: 0.926 },
  scale = { x: 1, y: 1, z: 1 },
  visible = true,
  onLoadTileSet = null,
  onLoadModel = null,
  onError = null,
  errorTarget = 1,
  loadSiblings = true,
  maxDepth = Infinity,
  autoCenter = true,
  frustumCulled = true,
  optimizeRaycast = true
}, ref) => {
  const tilesRendererRef = useRef(null);
  const groupRef = useRef(null);
  const animationIdRef = useRef(null);
  const hasRunRef = useRef(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);

  // Basic render loop as per documentation
  const renderLoop = () => {
    if (tilesRendererRef.current && camera && renderer) {

      tilesRendererRef.current.update();
    }

    animationIdRef.current = requestAnimationFrame(renderLoop);
  };

  const initializeTileset = () => {
    try {
      console.log('Initializing basic tileset:', url);

      if (!url) {
        console.warn('No tileset URL provided');
        return;
      }
      console.log('Loading tileset from URL:', url);
      const tilesRenderer = loadTileset(url);

      tilesRendererRef.current = tilesRenderer;
      
      // Configure the tiles renderer
      tilesRendererRef.current.setCamera(camera);
      tilesRendererRef.current.setResolutionFromRenderer(camera, renderer);

      // Performance settings
      tilesRendererRef.current.errorTarget = errorTarget;
      tilesRendererRef.current.loadSiblings = loadSiblings;
      tilesRendererRef.current.maxDepth = maxDepth;

      // Additional quality settings
      tilesRendererRef.current.frustumCulled = frustumCulled;
      tilesRendererRef.current.displayActiveTiles = false;
      tilesRendererRef.current.optimizeRaycast = optimizeRaycast;
      
      // Memory management (adjust based on your needs)
      tilesRendererRef.current.lruCache.minSize = 900;
      tilesRendererRef.current.lruCache.maxSize = 1300;
      // Create wrapper group for transformations
      const group = new THREE.Group();
      group.name = 'tileset-wrapper';


      // Add tileset group to our wrapper group
      group.add(tilesRendererRef.current.group);

      // Apply transforms
      group.position.set(position.x, position.y, position.z);
      group.rotation.set(rotation.x, rotation.y, rotation.z);

      group.scale.set(scale.x, scale.y, scale.z);
      group.visible = visible;
      groupRef.current = group;
      // Hide tiles within radius of 20 units from center using event
      // tilesRenderer.addEventListener('tile-visibility-change', function(event) {
      //   console.log('tile-visibility-change');

      // });
      // Event listeners
      tilesRenderer.addEventListener('load-tile-set', (event) => {
        const tileSet = event.tileSet;
        // console.log(groupRef.current)
        // console.log('tilesRenderer.group:', tilesRenderer.group);
        // groupRef.current.traverse(obj => {
        //   // console.log(obj.constructor.name);
        //   if (obj instanceof THREE.Mesh) {
        //     // Do something with the Mesh
        //     console.log('Found Mesh:', obj);
        //     const worldPos = new THREE.Vector3();
        //     obj.getWorldPosition(worldPos);
        //     console.log('Mesh world position:', worldPos);
        //   }
        // });
        // console.log('TileSet loaded:', tileSet);
        hasRunRef.current = true;
        setIsLoaded(true);
        if (autoCenter) {
          centerTileset();
        }
        if (onLoadTileSet) {
          onLoadTileSet(tileSet);
        }
      });
      tilesRenderer.addEventListener('load-model', event => {
        // event.content is the loaded mesh/group for this tile
        // console.log('Loaded model:')
        // console.log(groupRef.current)
        groupRef.current.traverse(obj => {
          if (obj instanceof THREE.Mesh) {
            // Do something with the Mesh
            // console.log('Found Mesh:', obj);
            const worldPos = new THREE.Vector3();
            obj.getWorldPosition(worldPos);
            // console.log('Mesh world position:');
            const radius = 50;
            const isBetweenCenter = worldPos.x < radius && worldPos.x > -radius && worldPos.z < radius && worldPos.z > -radius;
            if (isBetweenCenter) {
              obj.visible = false;
            }
          }
        });
      });
      if (onLoadModel) {
        tilesRenderer.onLoadModel = onLoadModel;
      }

      // Add to scene
      scene.add(group);

      // Start render loop
      if (!animationIdRef.current) {
        renderLoop();
      }

      console.log('Tileset initialized successfully');

    } catch (err) {
      console.error('Error initializing tileset:', err);
      setError(err.message);
      onError && onError(err);
    }
  };

  const centerTileset = () => {
    if (!tilesRendererRef.current || !groupRef.current) return;

    try {
      const box = new THREE.Box3();
      const hasBounds = tilesRendererRef.current.getBoundingBox(box);
      if (hasBounds) {
        // Center tileset only
        const center = box.getCenter(new THREE.Vector3());
        // console.log('Centering tileset to:', center.x );
        tilesRendererRef.current.group.position.copy(center).multiplyScalar(-1);
        // console.log(tilesRendererRef.current.group.position.x)
      } else {
        console.warn('Could not get tileset bounds');
      }
    } catch (err) {
      console.warn('Could not center tileset:', err);
    }
  };

  const cleanup = () => {
    // Stop render loop
    if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current);
      animationIdRef.current = null;
    }

    // Dispose of tileset
    if (tilesRendererRef.current) {
      // tilesRendererRef.current.dispose();
      tilesRendererRef.current = null;
    }

    // Remove from scene
    if (groupRef.current && scene) {
      scene.remove(groupRef.current);
      groupRef.current = null;
    }

    setIsLoaded(false);
    setError(null);
  };

  useEffect(() => {
    console.log('TileSet component mounted with URL:', url, scene, renderer, camera);

    if (!url || !scene || !renderer || !camera) {
      console.warn('TileSet: Missing required props', {
        url: !!url,
        scene: !!scene,
        renderer: !!renderer,
        camera: !!camera
      });
      return;
    }


    initializeTileset();

    // Picking logic for structural metadata
    const triangle = new THREE.Triangle();
    const barycoord = new THREE.Vector3();
    const invertedMatrix = new THREE.Matrix4();
    const raycaster = new THREE.Raycaster();
    const domElement = renderer.domElement;

    // function onClick(e) {
    //   // Get mouse position normalized to [-1, 1]
    //   // console.log(tilesRendererRef.current);
    //   // console.log(groupRef.current)
    //   const rect = domElement.getBoundingClientRect();
    //   const mouse = new THREE.Vector2(
    //     ((e.clientX - rect.left) / rect.width) * 2 - 1,
    //     -((e.clientY - rect.top) / rect.height) * 2 + 1
    //   );
    //   raycaster.setFromCamera(mouse, camera);
    //   const hits = raycaster.intersectObject(scene, true);
    //   if (hits.length > 0) {
    //     const { object, face, point, faceIndex } = hits[0];
    //     // Calculate barycoord of the point on the geometry face
    //     if (face && object.geometry && object.geometry.isBufferGeometry) {
    //       invertedMatrix.copy(object.matrixWorld).invert();
    //       const localPoint = point.clone().applyMatrix4(invertedMatrix);
    //       console.log('localPoint', localPoint);
    //       const pos = object.geometry.attributes.position;
    //       const a = new THREE.Vector3().fromBufferAttribute(pos, face.a);
    //       const b = new THREE.Vector3().fromBufferAttribute(pos, face.b);
    //       const c = new THREE.Vector3().fromBufferAttribute(pos, face.c);
    //       triangle.set(a, b, c);
    //       triangle.getBarycoord(localPoint, barycoord);
    //       console.log({triangle});
    //     }
    //     console.log({object})
    //     const { meshFeatures, structuralMetadata } = object.userData;
    //     if (meshFeatures && structuralMetadata) {
    //       meshFeatures.getFeaturesAsync(faceIndex, barycoord).then(features => {
    //         const tableIndices = meshFeatures.getFeatureInfo().map(info => info.propertyTable);
    //         const data = structuralMetadata.getPropertyTableData(tableIndices, features);
    //         console.log(data);
    //       });
    //     }
    //   }
    // }
    // domElement.addEventListener('click', onClick);

    // Cleanup event listener
    return () => {
      // domElement.removeEventListener('click', onClick);
      cleanup();
    };


    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, scene, renderer, camera]);

  // Update transforms when props change
  // useEffect(() => {
  //   if (groupRef.current) {
  //     groupRef.current.rotation.set(rotation.x, rotation.y, rotation.z);
  //     gui.init()
  //     gui.get(gui => {
  //       gui.add(groupRef.current.rotation, "x", -Math.PI, Math.PI).step(0.001);
  //       gui.add(groupRef.current.rotation, "y", -Math.PI, Math.PI).step(0.001);
  //       gui.add(groupRef.current.rotation, "z", -Math.PI, Math.PI).step(0.001);
  //       // .onChange(guiChanged);
  //     });

  //   }
  // }, [groupRef]);
  useEffect(() => {
    if (groupRef.current) {
      // groupRef.current.position.set(position.x, position.y, position.z);
      
      // groupRef.current.scale.set(scale.x, scale.y, scale.z);
      // console.log('Transform updated:', { position, rotation, scale });
    }
  }, [position, rotation, scale]);

  // Update visibility
  useEffect(() => {
    if (groupRef.current) {
      groupRef.current.visible = visible;
    }
  }, [visible]);


  return null;
});

TileSet.displayName = 'TileSet';

export default TileSet;